import { defineStore } from 'pinia';
import { ref, watch } from 'vue';
import type { DateGroupVisibilityRule } from '@yana/shared';
import { ApiError, api } from '@/api/client';
import { useAuthStore } from './auth';

type RuleInput = Pick<DateGroupVisibilityRule, 'scope' | 'dateFrom' | 'dateTo'>;

/** Серверные правила видимости; личные настройки не переживают смену сессии. */
export const useDateGroupVisibilityStore = defineStore('dateGroupVisibility', () => {
  const auth = useAuthStore();
  const items = ref<DateGroupVisibilityRule[]>([]);
  const loading = ref(false);
  const loaded = ref(false);
  const saving = ref(false);
  const error = ref<string | null>(null);
  let generation = 0;
  let request = 0;
  let revision = 0;
  let pending: Promise<void> | null = null;

  function reset(): void {
    generation++;
    request++;
    revision++;
    pending = null;
    items.value = [];
    loading.value = false;
    loaded.value = false;
    saving.value = false;
    error.value = null;
  }

  watch(() => auth.user?.id, reset, { flush: 'sync' });

  function load(force = false): Promise<void> {
    if (!auth.user) return Promise.resolve();
    if (!force && pending) return pending;
    if (!force && loaded.value) return Promise.resolve();
    const ownGeneration = generation;
    const ownRequest = ++request;
    const ownRevision = revision;
    loading.value = true;
    error.value = null;
    const isCurrent = () => generation === ownGeneration && request === ownRequest;
    pending = api.get<{ items: DateGroupVisibilityRule[] }>('/candidates/date-visibility')
      .then((response) => {
        if (!isCurrent() || revision !== ownRevision) return;
        items.value = response.items;
        loaded.value = true;
      })
      .catch((cause: unknown) => {
        if (!isCurrent() || revision !== ownRevision) return;
        error.value = cause instanceof ApiError ? cause.message : 'Не удалось загрузить скрытые даты';
        throw cause;
      })
      .finally(() => {
        if (!isCurrent()) return;
        loading.value = false;
        pending = null;
      });
    return pending;
  }

  async function mutate<T>(send: () => Promise<T>, apply: (result: T) => void): Promise<boolean> {
    if (saving.value) return false;
    const ownGeneration = generation;
    saving.value = true;
    error.value = null;
    try {
      const result = await send();
      if (generation !== ownGeneration) return false;
      revision++;
      apply(result);
      return true;
    } catch (cause) {
      if (generation !== ownGeneration) return false;
      error.value = cause instanceof ApiError ? cause.message : 'Не удалось изменить видимость дат';
      throw cause;
    } finally {
      if (generation === ownGeneration) saving.value = false;
    }
  }

  function add(input: RuleInput): Promise<boolean> {
    return mutate(
      () => api.post<DateGroupVisibilityRule>('/candidates/date-visibility', input),
      (rule) => { items.value = [...items.value.filter((item) => item.id !== rule.id), rule]; },
    );
  }

  function promote(id: string): Promise<boolean> {
    return mutate(
      () => api.patch<DateGroupVisibilityRule>(`/candidates/date-visibility/${id}`, { scope: 'all' }),
      (rule) => { items.value = items.value.map((item) => item.id === id ? rule : item); },
    );
  }

  function remove(id: string): Promise<boolean> {
    return mutate(
      () => api.delete(`/candidates/date-visibility/${id}`),
      () => { items.value = items.value.filter((item) => item.id !== id); },
    );
  }

  return { items, loading, loaded, saving, error, load, add, promote, remove, reset };
});
